// client/src/services/analyticsService.ts

import apiClient from "./api"; 

// TypeScript interfaces for analytics data

/**
 * Overall performance data.
 */
export interface PerformanceData {
  averageScore: number;
  totalStudyHours: number;
  rank: number;
  // Add additional fields as needed
}

/**
 * Comparison item used in benchmarks.
 */
export interface ComparisonItem {
  metric: string;
  currentValue: number;
  benchmark: number;
}

/**
 * Weekly progress visualization data.
 */
export interface ProgressVisualizationData {
  week: string;
  progress: number; // Percentage (0-100)
}

/**
 * Teacher-specific analytics data.
 */
export interface TeacherAnalyticsData {
  teacherId: string;
  averageScore: number;
  feedbackCount: number;
  engagementRate: number;
  // Add additional metrics if needed
}

/**
 * Fetches overall performance data.
 * Endpoint: GET /analytics/performance
 */
export const getPerformanceData = async (): Promise<PerformanceData> => {
  try {
    const response = await apiClient.get("/analytics/performance");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetches comparison data.
 * Endpoint: GET /analytics/comparison
 */
export const getComparisonData = async (): Promise<ComparisonItem[]> => {
  try {
    const response = await apiClient.get("/analytics/comparison");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetches progress visualization data.
 * Endpoint: GET /analytics/progress
 */
export const getProgressVisualizationData = async (): Promise<ProgressVisualizationData[]> => {
  try {
    const response = await apiClient.get("/analytics/progress");
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Fetches teacher analytics for a specific teacher.
 * Endpoint: GET /analytics/teacher/:teacherId
 */
export const getTeacherAnalytics = async (
  teacherId: string
): Promise<TeacherAnalyticsData> => {
  try {
    const response = await apiClient.get(`/analytics/teacher/${teacherId}`);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

/**
 * Utility function to handle API errors.
 */
const handleApiError = (error: unknown): void => {
  if (error instanceof Error) {
    console.error("Analytics Service Error:", error.message);
  } else {
    console.error("Unexpected Analytics Service Error:", error);
  }
};
